import {Component, EventEmitter, HostBinding, Inject, Input, OnInit, Output} from '@angular/core';
import {ProductionOrderCustomSettings} from '@domain/production-schedule/custom-settings/production-order-custom-settings';
import {DrawingType} from '@domain/production-schedule/drawing-type.enum';
import {ProductionSchedule} from '@domain/production-schedule/production-schedule';
import {ProductionScheduleDrawingViewMode} from '@domain/production-schedule/production-schedule-drawing-view-mode.enum';
import {ProductionScheduleItemInPathGroup} from '@domain/production-schedule/production-schedule-item-in-path-group';
import {ProductionScheduleOrderLine} from '@domain/production-schedule/production-schedule-order-line';
import {ProductionSchedulePath} from '@domain/production-schedule/production-schedule-path';
import {ProductionSchedulePathsOfColoredYarnSet} from '@domain/production-schedule/production-schedule-paths-of-colored-yarn-set';
import {ProductionSchedulePhase} from '@domain/production-schedule/production-schedule-phase.enum';
import {Permission} from '@domain/profile/permission.enum';
import {Subscription} from '@domain/profile/subscription';
import {TargetForListOfDrawingsEnum} from '@domain/target-for-list-of-drawings.enum';
import {OverviewListColoredYarnSetWithStartDent} from '@domain/textile-data/creel/overview-list-colored-yarn-set-with-start-dent';
import {PathWidth} from '@domain/textile-data/machine-quality/path-width';
import {AUTHENTICATION, Authentication} from '@infrastructure/http/authentication/authentication';
import {PRODUCTION_SCHEDULES, ProductionSchedules} from '@infrastructure/http/production-schedule/production-schedules';
import {OrderLinesCustomSettingsDialogComponent} from '@presentation/components/dialog-button/dialog-data/order-lines-settings/order-lines-settings.component';
import {ListDrawing} from '@presentation/components/drawing-list/list-drawing';
import {ListDrawingConfiguration} from '@presentation/components/drawing-list/list-drawing-configuration';
import {ListDrawingOrderLine} from '@presentation/components/drawing-list/list-drawing-order-line';
import {PRODUCTION_SCHEDULE_PLAN, ProductionSchedulePlan} from '@presentation/pages/texfab/production-schedule/add/plan/production-schedule-plan';
import {BaseComponent, GridModel, LocalStorageService, StatusBar, TranslateService, Unit} from '@vdw/angular-component-library';
import {some} from 'lodash-es';
import {distinctUntilChanged, map as rxjsMap, takeUntil} from 'rxjs/operators';
import {AddProductionScheduleNavigationState} from '../add-production-schedule-navigation-state.interface';

@Component({
  selector: 'app-production-schedule-drawing-library',
  templateUrl: './production-schedule-drawing-library.component.html',
  styleUrls: ['./production-schedule-drawing-library.component.scss']
})
export class ProductionScheduleDrawingLibraryComponent extends BaseComponent implements OnInit {
  @Input() public showSelectedItems = false;
  @Input() public productionSchedule: ProductionSchedule;
  @Input() public productionSchedulePhase: ProductionSchedulePhase;
  @Input() public productionOrderCustomSettings: ProductionOrderCustomSettings;
  @Input() public isPreSelection = false;
  @Input() public selectedPath: ProductionSchedulePath;
  @Input() public selectedRestPathColoredYarnSet: OverviewListColoredYarnSetWithStartDent;
  @Input() public productionSchedulePathsOfColoredYarnSet: ProductionSchedulePathsOfColoredYarnSet;
  @Input() public pathWidths: PathWidth[];
  @Input() public buggyLengthMaxInPicks: number;
  @Input() public availableSpaceInBuggyInPicks: number;
  @Input() public availableSpaceInBuggyInCommercialUnit: number;
  @Input() public autoFillingPath: boolean;
  @Input() public marginInDentsOfMissingDesigns: number;
  @Input() public preselectedDrawings: ListDrawingConfiguration[];
  @Input() public preselectedOrderlineIds: number[] = [];
  @Input() public preselectedDesignIds: number[] = [];
  @Input() public preselectedConfigurationIds: number[] = [];
  @Input() public addProductionScheduleNavigationState: AddProductionScheduleNavigationState = null;
  @Input() public selectedPathContentRowData: ProductionScheduleItemInPathGroup;
  @Output() public navigateBackOutput = new EventEmitter<any>();
  @Output() public selectedPathOutput: EventEmitter<ProductionSchedulePath> = new EventEmitter<ProductionSchedulePath>();
  @Output() public selectedRestPathColoredYarnSetOutput: EventEmitter<OverviewListColoredYarnSetWithStartDent> = new EventEmitter<OverviewListColoredYarnSetWithStartDent>();
  @Output() public selectedViewModeOutput: EventEmitter<ProductionScheduleDrawingViewMode> = new EventEmitter<ProductionScheduleDrawingViewMode>();
  @Output() public preselectedOrderlineIdsOutput = new EventEmitter<number[]>();
  @Output() public preselectedDrawingsOutput = new EventEmitter<ListDrawingConfiguration[]>();
  @Output() public preselectedDrawingIdsOutput = new EventEmitter<number[]>();
  @Output() public preselectedConfigurationIdsOutput = new EventEmitter<number[]>();

  @HostBinding('class.build-production-schedule-phase') public get isCurrentProductionSchedulePhaseBuild(): boolean {
    return this.productionSchedulePhase === ProductionSchedulePhase.BUILD;
  }

  public readonly listOfDrawingsTarget: TargetForListOfDrawingsEnum = TargetForListOfDrawingsEnum.PRODUCTION_ORDER;
  public listOfDrawings: ListDrawing[] = [];
  public statusBar: StatusBar;
  public isEditingPaths = false;
  public displayTechnicalUnit = true;
  public readonly filterOnBMPDrawingType = DrawingType.BMP;
  public viewModes: ProductionScheduleDrawingViewMode[] = [
    ProductionScheduleDrawingViewMode.ORDER_LINES,
    ProductionScheduleDrawingViewMode.DESIGNS_LIST,
    ProductionScheduleDrawingViewMode.GROUPED_ORDER_LINES
  ];

  public selectedViewMode = ProductionScheduleDrawingViewMode.ORDER_LINES;
  public orderlinesGridModel: GridModel;
  public readonly customSettingsComponent = OrderLinesCustomSettingsDialogComponent;
  public readonly commercialUnit = Unit.CENTIMETER;

  public constructor(
    @Inject(AUTHENTICATION) private readonly authentication: Authentication,
    @Inject(PRODUCTION_SCHEDULE_PLAN) private readonly productionSchedulePlan: ProductionSchedulePlan,
    @Inject(PRODUCTION_SCHEDULES) private readonly productionSchedules: ProductionSchedules,
    private readonly translate: TranslateService,
    private readonly localStorage: LocalStorageService
  ) {
    super();
  }

  public ngOnInit(): void {
    const currentSubscription = this.authentication.getCurrentSubscription();

    this.filterViewModes(currentSubscription);

    this.displayTechnicalUnit = this.isCurrentProductionSchedulePhaseBuild;
    this.subscribeToEditPathChanges();
    this.subscribeToProductionScheduleChanges();

    if (currentSubscription.hasPermission(Permission.PRODUCTION_ORDER_GROUPED_DESIGN_VIEW)) {
      this.viewModes.push(ProductionScheduleDrawingViewMode.GROUPED_DESIGNS);

      if (!this.isPreSelection) {
        this.viewModes.push(ProductionScheduleDrawingViewMode.GROUPED_DESIGN_THUMBNAILS);
      }
    }
    this.selectedViewMode = this.localStorage.get('drawingLibraryViewMode') || ProductionScheduleDrawingViewMode.ORDER_LINES;

    if ((this.isPreSelection && this.selectedViewMode === ProductionScheduleDrawingViewMode.GROUPED_DESIGN_THUMBNAILS) || !this.viewModes.includes(this.selectedViewMode)) {
      this.onSelectedViewModeChange(this.viewModes[0]);
    } else {
      this.onSelectedViewModeChange(this.selectedViewMode);
    }
  }

  public getOrderlinesTranslationKey(): string {
    return this.translate.instant('PRODUCTION_ORDER.DESIGN_LIBRARY.VIEW_MODES.ORDER_LINES');
  }

  public onSelectedViewModeChange(viewMode: ProductionScheduleDrawingViewMode): void {
    this.selectedViewMode = viewMode;
    this.localStorage.set('drawingLibraryViewMode', viewMode);
    this.selectedViewModeOutput.emit(viewMode);
  }

  public onDrawingSelected(selectedDrawings: ListDrawingConfiguration[]): void {
    this.preselectedDrawingsOutput.emit(selectedDrawings);
  }

  public onDrawingIdsSelected(selectedDrawingIds: number[]): void {
    this.preselectedDrawingIdsOutput.emit(selectedDrawingIds);
  }

  public onConfigurationIdsSelected(selectedConfigurationIds: number[]): void {
    this.preselectedConfigurationIdsOutput.emit(selectedConfigurationIds);
  }

  public onOrderlinesSelected(selectedOrderlineIds: number[]): void {
    this.preselectedOrderlineIds = selectedOrderlineIds;
    this.preselectedOrderlineIdsOutput.emit(this.preselectedOrderlineIds);
  }

  public isViewModeDesignsList(): boolean {
    return this.selectedViewMode === ProductionScheduleDrawingViewMode.DESIGNS_LIST;
  }

  public isViewModeGroupedDrawings(): boolean {
    return this.selectedViewMode === ProductionScheduleDrawingViewMode.GROUPED_DESIGNS;
  }

  public isViewModeNewOrderLines(): boolean {
    return this.selectedViewMode === ProductionScheduleDrawingViewMode.ORDER_LINES;
  }

  public isViewModeNewGroupedOrderLines(): boolean {
    return this.selectedViewMode === ProductionScheduleDrawingViewMode.GROUPED_ORDER_LINES;
  }

  public isViewModeNewGroupedDesignThumbnails(): boolean {
    return this.selectedViewMode === ProductionScheduleDrawingViewMode.GROUPED_DESIGN_THUMBNAILS;
  }

  public canSelectDrawings(): boolean {
    return !this.isCurrentProductionSchedulePhaseBuild || (this.isCurrentProductionSchedulePhaseBuild && !this.showSelectedItems);
  }

  private subscribeToProductionScheduleChanges(): void {
    this.productionSchedulePlan.currentUpdatedProductionSchedule
      .pipe(
        rxjsMap((productionSchedule: ProductionSchedule) => productionSchedule.orderLines),
        distinctUntilChanged((oldOrderLines: ProductionScheduleOrderLine[], newOrderLines: ProductionScheduleOrderLine[]) =>
          newOrderLines.every((newOrderLine: ProductionScheduleOrderLine) => some(oldOrderLines, {id: newOrderLine.id, amountInProductionSchedule: newOrderLine.amountInProductionSchedule}))
        ),
        takeUntil(this.unSubscribeOnViewDestroy)
      )
      .subscribe(() => this.updateOrderLinesAmountInProductionSchedule());
  }

  private filterViewModes(currentSubscription: Subscription): void {
    if (!currentSubscription.hasPermission(Permission.TEXFAB_ORDERBOOK)) {
      this.viewModes = this.viewModes.filter(
        (viewMode: ProductionScheduleDrawingViewMode) => viewMode !== ProductionScheduleDrawingViewMode.ORDER_LINES && viewMode !== ProductionScheduleDrawingViewMode.GROUPED_ORDER_LINES
      );
    }
  }

  private updateOrderLinesAmountInProductionSchedule(): void {
    this.listOfDrawings
      .filter((listDrawing: ListDrawing) => listDrawing instanceof ListDrawingOrderLine)
      .forEach((listDrawingOrderLine: ListDrawingOrderLine) => {
        listDrawingOrderLine.orderLine.amountInProductionSchedule = this.productionSchedule.getOrderLineQuantityInProductionSchedulePathsColoredYarnSets(listDrawingOrderLine.orderLine.id);
      });
  }

  private subscribeToEditPathChanges(): void {
    this.productionSchedulePlan.currentEditingPaths.pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe((isEditingPaths: boolean) => (this.isEditingPaths = isEditingPaths));
  }
}
